import React, { useState } from 'react';
import { authService } from '../services/authService';

const AuthenticationStatus = ({ user, onResendVerification }) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async () => {
    if (!user?.email) return;
    
    setResendLoading(true);
    setResendMessage('');
    
    try {
      const response = await authService.resendVerificationEmail(user.email);
      if (response.success) {
        setResendMessage('✅ 認証メールを再送信しました。メールをご確認ください。');
      } else {
        setResendMessage('❌ ' + (response.message || '認証メールの送信に失敗しました'));
      }
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setResendMessage('❌ 認証メールの送信に失敗しました');
    } finally {
      setResendLoading(false);
      if (onResendVerification) {
        onResendVerification();
      }
    }
  };

  if (!user) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">未ログイン</h3>
            <p className="text-sm text-gray-600">アカウントにログインしてください</p>
          </div>
        </div>
      </div>
    );
  }

  const isVerified = user.isEmailVerified;
  const plan = user.plan || 'free';

  // 認証済みの場合は何も表示しない
  if (isVerified) {
    return null;
  }

  // 未認証の場合（プランによって表示を変更）
  const isHighPriorityUser = plan === 'standard' || plan === 'premium';
  
  if (isHighPriorityUser) {
    // 有料プラン：緊急性を強調
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-red-800">⚠️ メール認証が必要です</h3>
            <p className="text-sm text-red-700 mt-1">
              <strong>{user.email}</strong> の認証が完了していません
            </p>
            <div className="mt-2 bg-white rounded-lg p-3 border border-red-200">
              <p className="text-sm text-red-800 font-medium mb-2">
                📌 {plan}プランをご利用いただくには認証が必要です
              </p>
              <ul className="text-xs text-red-700 space-y-1 mb-3">
                <li>• 支払い機能が制限されています</li>
                <li>• 一部の高度な機能が使用できません</li>
                <li>• セキュリティリスクが高い状態です</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {resendLoading ? '送信中...' : '📧 認証メール再送信'}
                </button>
              </div>
            </div>
            {resendMessage && (
              <div className={`mt-2 p-2 rounded-lg text-xs ${
                resendMessage.startsWith('✅') 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {resendMessage}
                {resendMessage.startsWith('✅') && (
                  <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
                    <p className="font-bold text-yellow-900 text-xs">
                      📧 <strong>迷惑メール・スパムフォルダ</strong>を必ずご確認ください！
                    </p>
                    <p className="text-yellow-800 text-xs mt-1">
                      Gmail・Yahoo!メールなどで迷惑メールに分類される可能性があります
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 無料プランの場合は何も表示しない
  return null;
};

export default AuthenticationStatus;